import { useState, useRef } from "react";
import { toast } from "sonner";
import { UploadCloud, Download, Loader2, FileArchive, X, ArrowRight } from "lucide-react";
import { api } from "../../lib/api";
import { handlePdfError } from "../../lib/pdf";
import { creditLabel } from "../../lib/pricing";
import { useOnline } from "../../hooks/usePWA";
import { Button } from "../ui/button";
import ToolIntro from "./ToolIntro";
import PreviewUnlock from "./PreviewUnlock";

const PRESETS = [
  { key: "ebook", label: "Balanced (recommended)", hint: "150 DPI — great for web & email" },
  { key: "screen", label: "Maximum compression", hint: "72 DPI — smallest size" },
  { key: "printer", label: "High quality", hint: "300 DPI — print-ready" },
];

const fmtSize = (b) => (b > 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

export default function CompressTab() {
  const [file, setFile] = useState(null);
  const [preset, setPreset] = useState("ebook");
  const [drag, setDrag] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [stage, setStage] = useState(null);
  const [result, setResult] = useState(null); // {prep_id, original_size, new_size, reduction_pct, cost, has_preview}
  const inputRef = useRef(null);
  const online = useOnline();

  const setPdf = (f) => {
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".pdf")) { toast.error("Please choose a PDF file"); return; }
    setFile(f); setResult(null);
  };

  const analyze = async () => {
    if (!file) { toast.error("Add a PDF first"); return; }
    setAnalyzing(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("preset", preset);
      const res = await api.post("/pdf/compress/prepare", form, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(res.data);
      if (res.data.reduction_pct <= 0) {
        toast.info("Already well optimized", { description: "This PDF can't be compressed much further." });
      }
    } catch (e) {
      handlePdfError(e);
    } finally {
      setAnalyzing(false);
    }
  };

  const approve = () => {
    if (!result?.prep_id) return;
    setStage(result);
  };

  return (
    <div className="max-w-2xl mx-auto">
      <ToolIntro
        Icon={FileArchive}
        title="PDF Compressor"
        desc="Shrink oversized PDFs (Canva exports, image-heavy lead magnets) so they're easy to email and upload. See the exact savings before you spend a credit."
        accepts="PDF up to 25 MB"
        tier="Basic"
        costLabel={creditLabel(result?.cost || 1)}
      />

      <div
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); setPdf(e.dataTransfer.files?.[0]); }}
        onClick={() => inputRef.current?.click()}
        data-testid="compress-dropzone"
        className={`cursor-pointer rounded-2xl border-2 border-dashed p-10 text-center transition-colors ${
          drag ? "border-indigo-500 bg-indigo-50" : "border-slate-300 bg-white hover:border-indigo-400 hover:bg-slate-50"
        }`}
      >
        <input ref={inputRef} data-testid="compress-input" type="file" accept=".pdf" className="hidden" onChange={(e) => setPdf(e.target.files?.[0])} />
        <div className="h-14 w-14 rounded-2xl bg-indigo-100 flex items-center justify-center mx-auto mb-4"><UploadCloud className="h-7 w-7 text-indigo-600" /></div>
        <p className="font-display text-lg font-semibold">{file ? file.name : "Drop a PDF or click to browse"}</p>
        <p className="text-slate-500 text-sm mt-1">{file ? `${fmtSize(file.size)} · ready to compress` : "We'll re-sample images and strip bloat."}</p>
      </div>

      {file && (
        <div className="mt-5">
          <p className="text-xs font-medium text-slate-500 mb-2">Compression level</p>
          <div className="grid sm:grid-cols-3 gap-2">
            {PRESETS.map((p) => (
              <button
                key={p.key}
                data-testid={`compress-preset-${p.key}`}
                onClick={() => { setPreset(p.key); setResult(null); }}
                className={`text-left rounded-xl border p-3 transition-colors ${
                  preset === p.key ? "border-indigo-600 ring-1 ring-indigo-600 bg-indigo-50" : "border-slate-200 bg-white hover:border-indigo-300"
                }`}
              >
                <div className="text-sm font-semibold">{p.label}</div>
                <div className="text-xs text-slate-500 mt-0.5">{p.hint}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      {result && (
        <div data-testid="compress-result" className="mt-5 rounded-2xl border border-slate-200 bg-white p-5">
          <div className="flex items-center justify-center gap-4 text-center">
            <div>
              <div className="text-xs text-slate-400 font-mono">ORIGINAL</div>
              <div className="text-lg font-bold text-slate-700">{fmtSize(result.original_size)}</div>
            </div>
            <ArrowRight className="h-5 w-5 text-slate-300" />
            <div>
              <div className="text-xs text-slate-400 font-mono">COMPRESSED</div>
              <div className="text-lg font-bold text-emerald-600">{fmtSize(result.new_size)}</div>
            </div>
            {result.reduction_pct > 0 && (
              <span className="ml-2 bg-emerald-100 text-emerald-700 text-sm font-bold px-3 py-1.5 rounded-full">−{result.reduction_pct}%</span>
            )}
          </div>
        </div>
      )}

      <div className="mt-5 flex justify-center gap-3">
        {!result ? (
          <Button data-testid="compress-analyze-button" onClick={analyze} disabled={analyzing || !file || !online} className="bg-slate-900 hover:bg-slate-800 text-white rounded-full px-8 h-12">
            {analyzing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileArchive className="h-4 w-4 mr-2" />}
            {analyzing ? "Compressing…" : "Compress & preview"}
          </Button>
        ) : (
          <Button data-testid="compress-approve-button" onClick={approve} disabled={!online} className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-full px-8 h-12">
            <Download className="h-4 w-4 mr-2" />
            Preview & Unlock · {creditLabel(result.cost)}
          </Button>
        )}
        {file && (
          <button data-testid="compress-reset" onClick={() => { setFile(null); setResult(null); }} className="text-slate-400 hover:text-red-500 flex items-center gap-1 text-sm">
            <X className="h-4 w-4" /> Clear
          </button>
        )}
      </div>

      {stage && <PreviewUnlock stage={stage} onClose={() => setStage(null)} onUnlocked={() => { setStage(null); setFile(null); setResult(null); }} />}
    </div>
  );
}
